// Tela de envio de material de venda pro cliente (DA-682). Duas colunas:
//   ESQUERDA — TUDO que escolhe: a faixa das promocoes prontas em cima e, embaixo, a
//              busca com a lista de produtos (o operador clica em ate 3).
//   DIREITA  — a PREVIA: o fly de verdade que o cliente vai receber, mais a legenda.
//
// 🔑 A previa existe porque mandar mensagem nao volta, e ate ela o operador apertava as
// cegas: a primeira vez que alguem via a arte era depois de o cliente ja ter recebido.
// Ela tambem resolve, na operacao, o item aberto na DA-681 — foto de fornecedor que
// entra na arte com moldura e numerador: agora da' pra ver e trocar de produto.
// ⛔ Ela vem do SERVIDOR, da mesma funcao que envia. Desenhar uma aproximacao aqui seria
// mais rapido e mentiria — o operador aprovaria uma coisa e sairia outra.
//
// 🔑 UM botao so', no rodape, e ele diz o que vai sair. Pedido dele. Com um botao por
// cartao a tela tinha sete gatilhos de acao irreversivel espalhados; com um so', a
// escolha vira EXCLUSIVA (promocao limpa os produtos e vice-versa) — um clique, um fly.
//
// ⚠️ Nada nasce marcado: tela que GRAVA abre com nada selecionado, senao o default vira
// a decisao (CLAUDE.md § Metodo).
//
// A busca alcanca todo produto ativo com foto e preco (~1.496). ⚠️ NAO trocar pela
// `/api/public/catalogo`: aquela e' a vitrine, filtra `postar_catalogo` e alcanca 129 —
// foi o que deixou este recurso sem uso de 13/06 a 07/09/2026 (DA-681).
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Package, AlertTriangle, Image as ImageIcon, Pencil, Trash2 } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { atendimentoApi, type FlySalvo } from '../lib/api';
import { api } from '../../../lib/api';
import { useDialog } from '../../../components/DialogProvider';
import { useSocket } from '../../../lib/socket';
import { contar, FLY_CATALOGO_PRODUTOS, ehCatalogoFly, motivoQuantidadeFly } from '@fiodecobre/shared';
import { BlocoSobreCliente, type BlocoSobreClienteRef } from './BlocoSobreCliente';
import type { Contato } from '../lib/tipos';

type Produto = {
  id: number;
  codigo_interno: string | null;
  // Ja vem resolvida pelo backend: a comercial quando existe, senao a interna.
  descricao: string;
  preco_pix: string;
  foto_url: string;
  grupo_nome: string | null;
  tem_pagina: boolean;
  /** ⚠️ `estoque_atual` do produto ([DA-707]) — informa, NAO trava: a casa vende sem estoque
   *  desde a [DA-329], e aqui o numero existe pra ele decidir o que vale anunciar. */
  estoque: string;
};

/** `12.000` vira `12`, `1.500` vira `1,5` — produto de metro tem fracao, o resto nao.
 *  Exportada: a busca da campanha de fly (`ModalCampanhaFly`) mostra o estoque com ela. */
export function fmtEstoque(v: string | number): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

type Promo = {
  id: number;
  sigla: string;
  grupo_nome: string;
  do_assunto: boolean;
  qtd_produtos: number;
  com_estoque: number;
  produtos: Array<{ descricao: string; foto_url: string | null }>;
  enviavel: boolean;
  motivo_bloqueio: string | null;
};

type Previa = {
  imagem: string;
  itens: Array<{ id: number; codigo_interno: string | null; descricao: string; preco_pix: string }>;
  tem_link: boolean;
};

// Os dois marcos REAIS que o servidor reporta. Entre eles a barra anda em listras: nem o
// Puppeteer nem o Baileys dizem quanto falta, e percentual inventado e' pior que barra
// nenhuma (DA-198 — "progresso honesto").
type Etapa = 'montando' | 'enviando' | 'entregue' | 'erro';

// ⚠️ O teto de CLIQUES e' o do catalogo (12), mas o que ENVIA e' 1 a 3 ou 12 exatos — decisao dele
// em 14/09/2026. Entre 4 e 11 a vendedora esta montando o catalogo: a lista deixa marcar e o
// rodape diz quantos faltam, em vez de travar o clique no 4o produto.
const MAX_PRODUTOS = FLY_CATALOGO_PRODUTOS;

function formatBRL(v: string | number): string {
  return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface Props {
  conversaId: number;
  nomeContato?: string | null;
  onClose: () => void;
  onEnviado?: () => void;
  /** Liga o bloco SOBRE O CLIENTE (nome + etiqueta). Sem ele a tela segue so' de envio. */
  contatoId?: number | null;
  /** O contato relido depois de gravar nome ou etiqueta — quem desenha o contato se atualiza. */
  onContatoAtualizado?: (c: Contato) => void;
}

export function ModalEnviarMaterial({ conversaId, nomeContato, onClose, onEnviado, contatoId, onContatoAtualizado }: Props) {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [escolhidos, setEscolhidos] = useState<Produto[]>([]);
  const [promoSel, setPromoSel] = useState<Promo | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [erroPrevia, setErroPrevia] = useState<string | null>(null);
  const [etapa, setEtapa] = useState<Etapa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // ⚠️ **FLYS GUARDADOS** ([DA-707]) — a lista e' da LOJA, nao minha (decisao dele). `nomeNovo`
  // com valor liga o campo de batismo: `null` e' "nao estou salvando", `''` seria um nome vazio.
  const [salvos, setSalvos] = useState<FlySalvo[]>([]);
  const [nomeNovo, setNomeNovo] = useState<string | null>(null);
  const [salvandoFly, setSalvandoFly] = useState(false);
  /**
   * O fly guardado que esta sendo CORRIGIDO ([DA-707], pedido dele: *"acrescentar nos Flys botao
   * de Editar e Excluir"*). Com valor, o rodape grava POR CIMA daquele; sem valor, cria um novo.
   *
   * ⚠️ E' o mesmo campo de nome e o mesmo botao — dois caminhos separados pra "guardar" dariam
   * duas telas pra manter, e uma delas ficaria pra tras na primeira mudanca.
   */
  const [editandoFly, setEditandoFly] = useState<FlySalvo | null>(null);

  /** O nome que o fly ganha sozinho: as duas primeiras palavras de cada produto, somadas. */
  function sugerirNome(): string {
    return escolhidos.map((p) => p.descricao.split(' ').slice(0, 2).join(' ')).join(' + ');
  }
  const { socket } = useSocket();
  const dialog = useDialog();
  const enviando = etapa === 'montando' || etapa === 'enviando';

  /**
   * SOBRE O CLIENTE — nome e etiqueta antes do produto (pedido dele, 24/09/2026: *"Ela pergunta
   * antes de enviar"*). O bloco mora em `BlocoSobreCliente`, a mesma peca do item "Etiqueta" do
   * menu da conversa; aqui fica so' o recolhimento do nome no Enviar ([DA-697] p.3).
   */
  const sobreClienteRef = useRef<BlocoSobreClienteRef | null>(null);
  async function salvarNome(): Promise<boolean> {
    return sobreClienteRef.current ? sobreClienteRef.current.salvarNome() : true;
  }

  // NAO QUERO (RDS chamados 1.29 / DA-712) — este cliente pediu pra nao receber promocao?
  //
  // ⚠️ A trava que VALE e' a do servidor (409 no `enviar-fly`): esta consulta so' evita que a
  // vendedora monte o fly inteiro com o cliente esperando pra descobrir no clique. Aba anterior
  // ao deploy nem faz esta pergunta ([DA-699]), e por isso a recusa nao pode morar so' aqui.
  //
  // ⛔ Falha da consulta NAO bloqueia: erra-se pro lado de OFERECER na tela e pro lado de NEGAR
  // no envio. Bloquear por indisponibilidade esconderia o material de quem nunca pediu pra sair.
  const [recusaPromo, setRecusaPromo] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    api
      .get<{ pode: boolean }>(`/api/nao-quer-receber/checar?conversa_id=${conversaId}`)
      .then((r) => {
        if (vivo && r && r.pode === false) {
          setRecusaPromo('Este cliente pediu para não receber promoções — o envio de material está bloqueado.');
        }
      })
      .catch(() => { /* indisponivel: segue oferecendo, o servidor recusa se for o caso */ });
    return () => { vivo = false; };
  }, [conversaId]);

  // A faixa `MEUS FLYS` ([DA-707]). Falha em silencio de proposito: nao poder LISTAR material
  // guardado nao pode impedir de mandar um fly novo, que e' pra que a tela existe.
  const recarregarSalvos = useCallback(() => {
    atendimentoApi.listarFlysSalvos().then(setSalvos).catch(() => {});
  }, []);
  useEffect(() => { recarregarSalvos(); }, [recarregarSalvos]);

  /**
   * Guardar a combinacao que esta na tela ([DA-707], pedido dele: *"tem Fly que vou querer salvar
   * e outros nao"* — por isso e' botao proprio, e nao efeito colateral do enviar).
   *
   * ⚠️ O nome nasce sugerido a partir dos produtos e ele edita — campo vazio num batismo faz a
   * lista virar `Fly 1`, `Fly 2`, e ai ninguem acha nada depois de dez.
   */
  /**
   * Guardar o fly que esta na tela.
   *
   * ⛔ **UM clique, e o nome sai pronto** ([DA-707], 10/09/2026 — corrigido no mesmo dia em que
   * subiu). O desenho anterior tinha DOIS passos: o botao `Salvar este fly` so' abria um campo de
   * nome, e quem gravava era um segundo botao ao lado. Ele relatou *"salvei um novo fly, mas ele
   * nao aparece na lista"* e o `access.log` mostrou o que aconteceu: **nenhum POST chegou** — o
   * gesto morreu no meio, entre o botao que diz "Salvar" e o que salva de verdade.
   *
   * ⚠️ E' a armadilha que a casa ja tinha catalogado ([DA-697] p.3): campo preenchido que so'
   * vale se um SEGUNDO botao for apertado se perde calado, e quem opera le como falha de
   * disciplina — quando o defeito e' o desenho cobrar dois gestos por uma coisa so'.
   *
   * ✅ O nome sai dos proprios produtos e **da' pra corrigir depois**, pelo lapis da lista — que
   * so' existe porque ele pediu Editar/Excluir na mesma leva. Renomear virou um gesto opcional,
   * em vez de um pedagio na frente do que ele queria fazer.
   */
  async function salvarFly() {
    const nome = (nomeNovo ?? sugerirNome()).trim();
    if (!nome || escolhidos.length === 0) return;
    setSalvandoFly(true);
    try {
      const corpo = { nome, produto_ids: escolhidos.map((p) => p.id) };
      // Com `editandoFly` grava POR CIMA daquele; sem, cria um novo ([DA-707]).
      if (editandoFly) await atendimentoApi.editarFlySalvo(editandoFly.id, corpo);
      else await atendimentoApi.salvarFly(corpo);
      setNomeNovo(null);
      setEditandoFly(null);
      recarregarSalvos();
      // ⚠️ O recibo do gesto: a linha aparecendo na coluna ao lado ja e' a prova, mas ela pode
      // nascer fora da vista se a lista estiver cheia — e o aviso diz onde foi parar e como
      // renomear, que e' o passo que deixou de ser obrigatorio.
      dialog.toast(editandoFly
        ? 'Fly atualizado.'
        : `Guardado em MEUS FLYS como "${nome}" — o lápis renomeia.`);
    } catch (e: any) {
      setErro(e?.message ?? 'Nao consegui guardar este fly');
    } finally {
      setSalvandoFly(false);
    }
  }

  /**
   * Editar um fly guardado: repoe os produtos dele na tela E abre o campo do nome ja preenchido,
   * com o rodape gravando por cima ([DA-707]).
   *
   * ⚠️ Desde 07/10/2026 (saiu o "Montar um fly agora") editar so' RENOMEIA: os produtos de um fly
   * guardado nao se trocam mais aqui. O que muda e' so' o destino
   * do botao, e ele DIZ isso ("Salvar alteracoes").
   */
  function editarFly(f: FlySalvo) {
    if (enviando || f.produtos.length === 0) return;
    setErro(null);
    setPromoSel(null);
    setEscolhidos(f.produtos.slice(0, MAX_PRODUTOS));
    setEditandoFly(f);
    setNomeNovo(f.nome);
  }

  /**
   * Clicar num fly guardado REPOE a escolha — nao manda.
   *
   * ⛔ O envio segue no mesmo botao de sempre, depois da previa carregar: um clique que mandasse
   * material direto pro cliente derrubaria a regra da tela ([DA-682] — *"nao mandar o que ele
   * nao viu"*), e ainda por cima a partir de uma lista onde o vizinho de baixo e' outro fly.
   * ⚠️ Repor a escolha e' o mesmo gesto de clicar nos produtos um a um: cai na mesma exclusao
   * com as promocoes, e a previa se refaz sozinha com o preco de hoje.
   */
  function usarFlySalvo(f: FlySalvo) {
    if (enviando || f.produtos.length === 0) return;
    setErro(null);
    setPromoSel(null);
    setNomeNovo(null);
    setEditandoFly(null);
    setEscolhidos(f.produtos.slice(0, MAX_PRODUTOS));
  }

  async function apagarFlySalvo(f: FlySalvo) {
    if (enviando) return;
    const ok = await dialog.confirmar(
      `Apagar o fly guardado "${f.nome}"?\n\nEle some da lista de todo mundo. Os produtos e o que ja foi enviado nao mudam.`,
      { titulo: 'Apagar fly guardado?', textoConfirmar: 'Apagar' },
    );
    if (!ok) return;
    try {
      await atendimentoApi.apagarFlySalvo(f.id);
      recarregarSalvos();
    } catch (e: any) {
      setErro(e?.message ?? 'Nao consegui apagar este fly');
    }
  }

  // Progresso vem por socket, na sala do proprio operador (DA-682). ⚠️ Ignorar evento de
  // OUTRA conversa: a vendedora pode ter duas abas, e a barra desta tela so' descreve
  // este envio.
  useEffect(() => {
    if (!socket) return;
    const aoProgredir = (p: { conversa_id: number; etapa: Etapa }) => {
      if (p.conversa_id !== conversaId) return;
      setEtapa((atual) => (atual === null ? atual : p.etapa));
    };
    socket.on('fly_progresso', aoProgredir);
    return () => { socket.off('fly_progresso', aoProgredir); };
  }, [socket, conversaId]);

  useEffect(() => {
    atendimentoApi.promocoesParaEnvio(conversaId)
      .then((r) => setPromos(r as Promo[]))
      .catch(() => setPromos([]));
  }, [conversaId]);

  // A PREVIA. Respiro de 400ms depois do ultimo clique: marcar 3 produtos gera UM fly, nao
  // tres — gerar custa 0,7 a 2,3s (medido em prod). A mesma guarda de corrida da busca,
  // pelo mesmo motivo: aqui ela e' pior, porque a imagem velha chegando por ultimo
  // mostraria uma arte que nao corresponde ao que esta marcado.
  const reqPrevia = useRef(0);
  const chaveEscolha = promoSel ? `p${promoSel.id}` : escolhidos.map((p) => p.id).join('-');
  useEffect(() => {
    // De 4 a 11 nao ha arte a desenhar (o servidor recusaria): a previa espera o 12o produto.
    const corpo = promoSel
      ? { promocao_id: promoSel.id }
      : escolhidos.length > 0 && !motivoQuantidadeFly(escolhidos.length)
        ? { produto_ids: escolhidos.map((p) => p.id) }
        : null;
    if (!corpo) { setPrevia(null); setErroPrevia(null); setCarregandoPrevia(false); return; }
    const req = ++reqPrevia.current;
    setCarregandoPrevia(true);
    setErroPrevia(null);
    const timer = setTimeout(() => {
      atendimentoApi.flyPrevia(conversaId, corpo)
        .then((r) => { if (req === reqPrevia.current) setPrevia(r as Previa); })
        .catch((e: unknown) => {
          if (req !== reqPrevia.current) return;
          setPrevia(null);
          setErroPrevia((e as { message?: string })?.message || 'Nao consegui desenhar a previa');
        })
        .finally(() => { if (req === reqPrevia.current) setCarregandoPrevia(false); });
    }, 400);
    return () => clearTimeout(timer);
    // `chaveEscolha` resume a escolha: sem ela o efeito dispara a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveEscolha, conversaId]);

  function escolherPromo(pr: Promo) {
    if (enviando || !pr.enviavel) return;
    setErro(null);
    setEscolhidos([]);
    setPromoSel((atual) => (atual?.id === pr.id ? null : pr));
  }

  async function enviar() {
    if (enviando) return;
    const corpo = promoSel
      ? { promocao_id: promoSel.id }
      : escolhidos.length > 0
        ? { produto_ids: escolhidos.map((p) => p.id) }
        : null;
    if (!corpo) return;
    setErro(null);
    // Recolhe o nome digitado ANTES de mandar: o fly nao volta, o nome perdido tambem nao.
    if (!(await salvarNome())) return;
    setEtapa('montando');
    try {
      await atendimentoApi.enviarFly(conversaId, corpo);
      onEnviado?.();
      onClose();
    } catch (e: unknown) {
      setEtapa(null);
      setErro((e as { message?: string })?.message || 'Falha ao enviar');
    }
  }

  const promoDoAssunto = promos.find((p) => p.do_assunto) ?? null;
  const nada = escolhidos.length === 0 && !promoSel;
  // O que falta pra quantidade escolhida virar fly (4 a 11 = catalogo pela metade). Vale tambem pro
  // Salvar: fly guardado com 7 apareceria na Campanha e nao desenharia.
  const motivoQtd = !promoSel && escolhidos.length > 0 ? motivoQuantidadeFly(escolhidos.length) : null;
  const catalogo = !promoSel && ehCatalogoFly(escolhidos.length);
  // ⛔ O botao NAO acende enquanto a previa nao ficou pronta: mandar o que ele ainda nao
  // viu derrota o motivo de a previa existir, e o custo e' menos de 2 segundos.
  const podeEnviar = !nada && !motivoQtd && !!previa && !carregandoPrevia && !recusaPromo;
  const rotuloBotao = promoSel
    ? `Enviar promoção ${promoSel.grupo_nome}`
    : catalogo
      ? `Enviar catálogo com ${escolhidos.length} produtos`
      : escolhidos.length > 0
        ? `Enviar fly com ${contar(escolhidos.length, 'produto')}`
        : 'Enviar';
  // NAO QUERO (RDS chamados 1.29 / DA-712) tem que vir ANTES de qualquer outra dica: enquanto
  // ele valer, nada do que a vendedora escolher vai sair, e deixar a tela pedindo "escolha um
  // produto" mandaria ela montar um fly que o servidor vai recusar com o cliente esperando.
  const dica = recusaPromo
    ? recusaPromo
    : nada
    ? 'Escolha uma promoção ou um fly guardado — a prévia mostra o que o cliente recebe.'
    : motivoQtd
      ? `${escolhidos.length} produtos escolhidos. ${motivoQtd}`
    : erroPrevia
      ? erroPrevia
      : carregandoPrevia
        ? 'Desenhando a prévia...'
        : promoSel
          ? 'Vai o fly de 3 produtos desta promoção.'
          : catalogo
            ? `Catálogo com ${escolhidos.length} produtos — vai só a imagem, sem a lista na legenda.`
            : `Fly guardado com ${contar(escolhidos.length, 'produto')}.`;

  const rodape = (
    // ⚠️ `paddingRight` reserva o canto do CARIMBO DE VERSAO que o `telaCheia` desenha ali
    // (DA-456). Sem isso o botao fica POR CIMA dele — medido em prod, `elementFromPoint`
    // no centro do carimbo devolvia BUTTON.
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', paddingRight: 170 }}>
      {enviando ? <BarraProgresso etapa={etapa!} /> : (
        <>
          <span style={{ flex: 1, fontSize: 12, color: 'var(--text-color)', opacity: 0.85 }}>
            {erro ? <span style={{ color: '#b91c1c', fontWeight: 700 }}>{erro}</span> : dica}
          </span>
          {/* ⚠️ **GUARDAR e' gesto proprio** ([DA-707], regra dele: *"tem Fly que vou querer
              salvar e outros nao"*) — logo, botao separado e nunca efeito colateral do Enviar.
              ⛔ **Secundario ao lado do primario, nao sozinho**: e' o par que o `btn-secundario`
              precisa pra ser lido como botao ([DA-667] — colado num titulo ele vira parte do
              titulo). Aqui ele contrasta com o Enviar ao lado.
              ⚠️ So' aparece com produto escolhido: promocao pronta ja esta guardada por
              definicao, e faixa de escolha vazia nao tem o que batizar. */}
          {escolhidos.length > 0 && (
            !editandoFly ? (
              /* ⛔ "Salvar este fly" SAIU junto com o "Montar um fly agora" (07/10/2026): sem a
                 montagem, o que esta escolhido aqui ja' e' um fly guardado, e salvar de novo so'
                 criaria uma copia dele. Renomear segue pelo lapis da lista. */
              null
            ) : (
              /* ⚠️ Em edicao o rodape DIZ qual fly vai ser regravado — sem o nome ali, "Salvar
                 alteracoes" nao responde "alteracoes em QUE?", e o gesto passa a depender de o
                 operador lembrar em qual linha clicou. */
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flex: '0 0 auto' }}>
                <span style={{ fontSize: 11, opacity: 0.8, whiteSpace: 'nowrap' }}>editando</span>
                <input
                  autoFocus
                  value={nomeNovo ?? editandoFly.nome}
                  maxLength={80}
                  onChange={(e) => setNomeNovo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); void salvarFly(); }
                    if (e.key === 'Escape') { e.preventDefault(); setNomeNovo(null); setEditandoFly(null); }
                  }}
                  style={{ width: 200, fontSize: 16 }}
                />
                <button
                  type="button"
                  className="btn-secundario"
                  disabled={salvandoFly || !!motivoQtd || !(nomeNovo ?? editandoFly.nome).trim()}
                  title={motivoQtd ?? undefined}
                  onClick={() => void salvarFly()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {salvandoFly ? <Loader2 size={14} className="animate-spin" /> : 'Salvar alterações'}
                </button>
                <button
                  type="button"
                  className="btn-secundario"
                  onClick={() => { setNomeNovo(null); setEditandoFly(null); }}
                >
                  Cancelar
                </button>
              </span>
            )
          )}
          <button
            type="button"
            className="btn-primario"
            disabled={!podeEnviar}
            onClick={enviar}
            style={{ flex: '0 0 auto', padding: '11px 34px', fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap' }}
          >
            {rotuloBotao}
          </button>
        </>
      )}
    </div>
  );

  return (
    <Modal
      aberto
      telaCheia
      onFechar={() => { if (!enviando) onClose(); }}
      titulo={`Enviar material${nomeContato ? ` — ${nomeContato}` : ''}`}
      rodape={rodape}
      bodyStyle={{ overflow: 'hidden', padding: 0 }}
    >
      <div
        style={{
          height: '100%',
          display: 'grid',
          // minmax(0,...) em TODAS: sem isso a coluna com o item mais largo trava no tamanho
          // dele e come a largura das outras (DA-602).
          //
          // ⚠️ **TRES colunas desde 10/09/2026** ([DA-707], pedido dele: *"retirar os modelos
          // salvos da horizontal e colocar numa coluna vertical, de forma que se encher eu
          // consigo rolar a lista"*). Os numeros sao os DELE — *"encolher a primeira coluna da
          // pesquisa em 30% e a do preview em 20%"* — e as fracoes aqui sao exatamente isso
          // aplicado ao que havia antes (1.25fr e 1fr): 1.25 × 0.7 = **0.875**, 1 × 0.8 = **0.8**,
          // e o que sobra da soma 2.25 vira a coluna do meio: **0.575**.
          //
          // ✅ MEDIDO na janela dele (1910) antes de mexer: a esquerda cai de 1061 pra **743**, a
          // previa de 849 pra **679**, e a coluna nova nasce com **488**.
          //   • O pior nome de produto da base pede **509px** e a celula encolhida da **554** —
          //     passa, e o `truncate` ja cobria o resto.
          //   • ⛔ A ARTE NAO ENCOLHE UM PIXEL: ela tem teto de `maxWidth: 420` (mais abaixo),
          //     e a previa encolhida ainda oferece 647. So' abaixo de ~1280px de janela ela
          //     comeca a ceder — e ai cede por causa da janela, nao desta mudanca.
          gridTemplateColumns: 'minmax(0, 0.875fr) minmax(0, 0.575fr) minmax(0, 0.8fr)',
          // ⚠️ **Duas LINHAS desde 10/09/2026** ([DA-707], ajuste dele: *"vamos deixar so' os
          // nossos Flys"* na coluna do meio). As promocoes do Marketing voltaram pra faixa em
          // cima da busca — `auto` na primeira linha faz ela ocupar so' o que precisa, e a
          // segunda (`1fr`) fica com todo o resto pra lista de produtos.
          // ⛔ **As promocoes NAO sumiram da tela**: tirar a faixa sem repor o gesto deixaria a
          // tela sem a unica porta de mandar promocao pronta ([DA-705] — remover um elemento
          // pode desligar a unica porta de uma acao). O que ele pediu foi a COLUNA so' dos flys.
          gridTemplateRows: 'auto minmax(0, 1fr)',
          overflow: 'hidden',
        }}
      >
        {/* ============ FAIXA DAS PROMOCOES — em cima da busca, coluna 1 / linha 1 ============
            ⚠️ Voltou pra ca em 10/09/2026 ([DA-707], ajuste dele: a coluna do meio ficou so' com
            os flys da loja). Ela e' a UNICA porta de mandar uma promocao pronta — sumir daqui
            desligaria o recurso, e o pedido era sobre a coluna, nao sobre a promocao ([DA-705]).
            ⚠️ `flexWrap` (mais abaixo) segue valendo: na coluna encolhida as 6 de hoje nao cabem
            numa linha so' e quebram na segunda — perder altura se ve, perder promocao nao
            ([DA-655]). */}
        <div style={{ gridColumn: 1, gridRow: 1, padding: '12px 16px 0 16px', maxHeight: 170, overflowY: 'auto' }}>
          <div>
            {/* ⚠️ A peca do assunto ganha destaque, e o DESTAQUE precisa se explicar: borda
                diferente sem texto obriga o operador a adivinhar por que aquela e' outra.
                Na faixa compacta nao cabe um selo por peca, entao a explicacao vem aqui,
                nomeando qual e'. */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, fontSize: 12, color: 'var(--text-color)' }}>PROMOÇÕES PRONTAS</span>
              {promoDoAssunto ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: '#000', background: '#fde68a', padding: '1px 8px', borderRadius: 10 }}>
                  {promoDoAssunto.grupo_nome} é o assunto desta conversa
                </span>
              ) : (
                <span style={{ fontSize: 11, color: 'var(--text-color)', opacity: 0.75 }}>Marketing · Promoções</span>
              )}
            </div>
            {promos.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-color)', opacity: 0.8, padding: '6px 0' }}>
                Nenhuma promoção ativa.
              </div>
            ) : (
              // ⛔ QUEBRA de linha, nunca rolagem horizontal ([DA-655] no eixo deitado): rolagem
              // deitada ninguem procura. Na coluna encolhida ([DA-707]) as 6 de hoje nao cabem
              // numa linha e passam pra segunda — o custo e' honesto e visivel (a lista de
              // produtos perde altura), ao contrario de uma promocao escondida fora da borda.
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {promos.map((pr) => {
                  const sel = promoSel?.id === pr.id;
                  return (
                    <button
                      key={pr.id}
                      type="button"
                      onClick={() => escolherPromo(pr)}
                      disabled={enviando || !pr.enviavel}
                      title={pr.motivo_bloqueio ?? pr.grupo_nome}
                      style={{
                        flex: '0 0 auto', textAlign: 'left', padding: '5px 7px', borderRadius: 9,
                        display: 'flex', alignItems: 'center', gap: 6,
                        border: sel ? '3px solid #000' : pr.do_assunto ? '2px solid var(--primary)' : '1px solid var(--border)',
                        background: sel ? '#fde68a' : pr.do_assunto ? 'rgba(255,255,255,0.55)' : 'transparent',
                        opacity: pr.enviavel ? 1 : 0.5,
                        cursor: pr.enviavel ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {/* As fotos vem PRIMEIRO na linha: e' por elas que ele reconhece a promocao
                          de relance, e alinhadas na mesma abcissa a coluna se le de cima a baixo
                          num passe de olho — o mesmo motivo que fez a etiqueta virar coluna na
                          Gestao de Vendas ([DA-691]). */}
                      <div style={{ display: 'flex', gap: 3, flex: '0 0 auto' }}>
                        {pr.produtos.slice(0, 3).map((pp, i) => (
                          <div key={i} style={{ width: 28, height: 28, background: '#fff', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            {pp.foto_url
                              ? <img src={pp.foto_url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                              : <Package size={12} style={{ color: 'var(--text-muted)' }} />}
                          </div>
                        ))}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{
                            background: sel ? '#000' : 'var(--grid-header-bg)',
                            color: sel ? '#fde68a' : 'var(--grid-header-fg)',
                            fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 3, flex: '0 0 auto',
                          }}>
                            {pr.sigla}
                          </span>
                          <span style={{
                            fontSize: 11, fontWeight: 700, color: sel ? '#000' : 'var(--text-color)',
                            whiteSpace: 'nowrap',
                          }}>
                            {pr.grupo_nome}
                          </span>
                        </div>
                        {/* Promocao travada NAO some: fica apagada com o motivo (DA-468). */}
                        {!pr.enviavel && pr.motivo_bloqueio && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 3, fontSize: 9, fontWeight: 700, color: '#000', background: '#f97316', padding: '2px 5px', borderRadius: 8 }}>
                            <AlertTriangle size={9} /> {pr.motivo_bloqueio}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

        </div>

        {/* ============ MEIO — SO' os nossos flys, em coluna ROLAVEL ============
            ⚠️ Ajuste dele em 10/09/2026 ([DA-707]): *"vamos deixar so' os nossos Flys"*. As
            promocoes do Marketing voltaram pra faixa em cima da busca — elas continuam a um
            clique, so' nao disputam esta coluna.
            ⛔ **`gridColumn` explicito, e a coluna ATRAVESSA as duas linhas** (`1 / 3`): ela vem
            antes da esquerda no codigo e depois dela na tela, e sobe ate o topo pra ficar do
            tamanho da tela inteira — material guardado e' o que mais cresce aqui.
            ⚠️ `overflowY: auto` na coluna: e' o que ele pediu — *"se encher eu consigo rolar a
            lista"* —, e rolagem VERTICAL e' a que o operador procura ([DA-655]). */}
        <div style={{
          gridColumn: 2, gridRow: '1 / 3', padding: '12px 14px', display: 'flex',
          flexDirection: 'column', minHeight: 0, gap: 8, overflowY: 'auto',
          borderLeft: '1px solid var(--page-bg)', borderRight: '1px solid var(--page-bg)',
        }}>
          <div style={{ marginBottom: 2 }}>
            <span style={{ fontWeight: 800, fontSize: 12, color: 'var(--text-color)' }}>MEUS FLYS</span>
            <div style={{ fontSize: 11, color: 'var(--text-color)', opacity: 0.7 }}>
              Guardados pela loja · clique para repor a escolha
            </div>
          </div>

          {/* ⚠️ O vazio DIZ o gesto que o enche — a coluna existe sempre agora, e coluna em
              branco sem explicacao le como recurso quebrado ([DA-468]). */}
          {salvos.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-color)', opacity: 0.75, lineHeight: 1.5 }}>
              Nenhum fly guardado.<br />
              Monte e guarde na <strong>Campanha de fly</strong> (botão Salvar este fly) — ele
              aparece aqui para mandar quando quiser.
            </div>
          ) : (
            <>
              <div>
                {/* ⚠️ Em pe, como as promocoes acima ([DA-707]): a coluna inteira rola, entao
                    material guardado nao tem teto — que era o limite da faixa deitada. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {salvos.map((f) => {
                    const morto = f.produtos.length === 0;
                    return (
                      <span
                        key={f.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                          border: '1px solid var(--border, rgba(0,0,0,0.15))', borderRadius: 8,
                          padding: '3px 6px 3px 3px', opacity: morto ? 0.5 : 1,
                        }}
                        title={[
                          f.nome,
                          f.produtos.map((p) => p.descricao).join(' · '),
                          f.autor ? `Guardado por ${f.autor}` : null,
                        ].filter(Boolean).join('\n')}
                      >
                        <button
                          type="button"
                          onClick={() => usarFlySalvo(f)}
                          disabled={enviando || morto}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6, border: 'none',
                            background: 'transparent', padding: 0, minWidth: 0, flex: 1,
                            textAlign: 'left',
                            cursor: morto ? 'not-allowed' : 'pointer', color: 'var(--text-color)',
                          }}
                        >
                          {f.produtos.slice(0, 3).map((p) => (
                            <img
                              key={p.id}
                              src={p.foto_url}
                              alt=""
                              style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', background: '#fff', flex: '0 0 auto' }}
                            />
                          ))}
                          <span style={{ fontSize: 11, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.nome}
                          </span>
                        </button>
                        {/* ⚠️ **O que sumiu se DIZ** ([DA-468]): produto inativado depois de
                            guardado nao apaga o fly, mas quem clica precisa saber que vai mandar
                            menos do que montou. */}
                        {f.faltando > 0 && (
                          <span style={{ fontSize: 10, fontWeight: 700, background: '#fde68a', color: '#000', borderRadius: 999, padding: '0 5px', flex: '0 0 auto' }}>
                            {morto ? 'saiu do ar' : `−${f.faltando}`}
                          </span>
                        )}
                        {/* ⚠️ **EDITAR e EXCLUIR por linha** ([DA-707], pedido dele). Sao icones
                            e nao texto porque a coluna tem 488px e o nome do fly e' o que precisa
                            do espaco; o `title` de cada um diz o que faz e sobre QUAL fly.
                            ⛔ Acao POR LINHA, nunca uma barra de acoes que age no "selecionado":
                            ali o alvo e' um estado invisivel, e o gesto errado so' aparece depois
                            de feito ([DA-679] p.4 — a marcacao E' a decisao). */}
                        <button
                          type="button"
                          onClick={() => editarFly(f)}
                          disabled={enviando || morto}
                          title={`Renomear o fly guardado "${f.nome}"`}
                          style={{ border: 'none', background: 'transparent', padding: 0, display: 'flex', alignItems: 'center', color: 'var(--text-muted)', cursor: 'pointer', flex: '0 0 auto' }}
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => void apagarFlySalvo(f)}
                          disabled={enviando}
                          title={`Excluir o fly guardado "${f.nome}"`}
                          style={{ border: 'none', background: 'transparent', padding: 0, display: 'flex', alignItems: 'center', color: 'var(--text-muted)', cursor: 'pointer', flex: '0 0 auto' }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            </>
          )}

        </div>

        {/* ============ ESQUERDA — a busca e a montagem (coluna 1 / linha 2) ============
            ⚠️ Encolheu 30% por pedido dele ([DA-707]), e o pior nome de produto da base (509px)
            ainda cabe nos 554 que sobram — medido na tela publicada antes de mexer. Em cima dela,
            na linha 1, fica a faixa das promocoes. */}
        <div style={{ gridColumn: 1, gridRow: 2, padding: '0 16px 12px 16px', display: 'flex', flexDirection: 'column', minHeight: 0, gap: 10 }}>
          {/* ⚠️ SOBRE O CLIENTE — ANTES do produto, onde ele apontou (24/09/2026). A etiqueta
              e' combo da casa (lista em painel `fixed`, entao nao e' cortada pelo `overflow`
              desta coluna) e ja mostra as que o contato tem; o nome nasce com o gravado. */}
          {contatoId ? (
            <div style={{ flex: '0 0 auto', paddingTop: 10 }}>
              <BlocoSobreCliente ref={sobreClienteRef} contatoId={contatoId} desabilitado={enviando}
                onContatoAtualizado={onContatoAtualizado} />
            </div>
          ) : null}
          {/* ⛔ "MONTAR UM FLY AGORA" SAIU DAQUI em 07/10/2026 — pedido dele: o fly montado no chat
              anunciava o preco Pix do cadastro e o orcamento do mesmo produto cobrava outro (22,00
              contra 22,50). O fly avulso passou a sair do ORCAMENTO (caixa "Fly" ao lado do
              WhatsApp), com o preco do proprio orcamento. Promocoes prontas e Meus flys ficam.
              ⚠️ O gesto nao some calado ([DA-705]): esta nota diz onde ele foi parar. */}
          <div style={{ flex: '0 0 auto', paddingTop: 10, fontSize: 12, color: 'var(--text-color)', lineHeight: 1.5 }}>
            <span style={{ fontWeight: 800 }}>FLY DE UM PRODUTO</span><br />
            Monte no <strong>Orçamento</strong>: marque a caixa <strong>Fly</strong> ao lado do botão
            WhatsApp. O fly sai com o preço do orçamento — o mesmo que o caixa vai cobrar.
          </div>
        </div>

        {/* ============ DIREITA — a previa do que vai sair ============ */}
        <div style={{ gridColumn: 3, gridRow: '1 / 3', padding: '12px 16px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
            <span style={{ fontWeight: 800, fontSize: 12, color: 'var(--text-color)' }}>O QUE O CLIENTE VAI RECEBER</span>
            {/* ⚠️ O MOTIVO de nao ter link muda com o caso, e um texto so' mentiria: numa
                promocao nao ha link porque sao tres produtos, nao porque algum deixou de
                ser publicado. Texto que afirma a causa errada e' pior que texto nenhum. */}
            {previa && (
              <span style={{ fontSize: 11, color: 'var(--text-color)', opacity: 0.75 }}>
                {previa.tem_link
                  ? 'com o link do catálogo'
                  : previa.itens.length > 1
                    ? 'sem link — o fly leva mais de um produto'
                    : 'sem link — este produto não tem página no catálogo'}
              </span>
            )}
          </div>
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            {carregandoPrevia ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, paddingTop: 40 }}>
                <Loader2 size={26} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
                <span style={{ fontSize: 12, color: 'var(--text-color)', opacity: 0.8 }}>Desenhando a arte...</span>
              </div>
            ) : erroPrevia ? (
              <div style={{ paddingTop: 40, textAlign: 'center', maxWidth: 300 }}>
                <AlertTriangle size={26} style={{ color: '#f97316' }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)', marginTop: 8 }}>{erroPrevia}</div>
              </div>
            ) : previa ? (
              <>
                {/* ⚠️ Sem `aspectRatio` fixo: o catalogo e' 1080x1600 em pe, e o quadrado de antes o
                    encolheria dentro de uma caixa com faixas vazias dos lados. A proporcao sai da
                    propria imagem; a coluna ja rola. */}
                <img
                  src={previa.imagem}
                  alt="Previa do fly"
                  style={{ width: '100%', maxWidth: 420, height: 'auto', borderRadius: 10, boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }}
                />
                {/* A LEGENDA vai junto da imagem no WhatsApp — mostrar so' a arte deixaria
                    metade da mensagem sem conferencia. No catalogo ela nao lista os produtos
                    (decisao dele, 14/09/2026), entao a caixa nao aparece. */}
                {!ehCatalogoFly(previa.itens.length) && (
                <div style={{ width: '100%', maxWidth: 420, background: 'rgba(255,255,255,0.6)', border: '1px solid var(--border)', borderRadius: 9, padding: '9px 11px' }}>
                  {previa.itens.map((it) => (
                    <div key={it.id} style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-color)' }}>{it.descricao}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-color)', opacity: 0.85 }}>
                        R$ {formatBRL(it.preco_pix)} no Pix ou dinheiro{it.codigo_interno ? ` · Cod ${it.codigo_interno}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, paddingTop: 50, textAlign: 'center', maxWidth: 300 }}>
                <ImageIcon size={30} style={{ color: 'var(--text-muted)' }} />
                <span style={{ fontSize: 13, color: 'var(--text-color)', opacity: 0.8 }}>
                  Escolha um produto ou uma promoção para ver aqui a arte exata que vai pro cliente.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Passo REAL onde ha um (arte pronta / entregue) e listras entre eles. A largura NAO
// e' percentual de conclusao: ela so' marca em que trecho o envio esta.
function BarraProgresso({ etapa }: { etapa: Etapa }) {
  const passos: Array<{ chave: Etapa; texto: string }> = [
    { chave: 'montando', texto: 'Montando a arte' },
    { chave: 'enviando', texto: 'Enviando ao WhatsApp' },
    { chave: 'entregue', texto: 'Entregue' },
  ];
  const atual = passos.findIndex((p) => p.chave === etapa);
  const largura = etapa === 'entregue' ? '100%' : etapa === 'enviando' ? '62%' : '22%';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 22, width: '100%' }}>
      <div style={{ display: 'flex', gap: 22, flex: '0 0 auto' }}>
        {passos.map((p, i) => (
          <div
            key={p.chave}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
              fontWeight: i === atual ? 800 : 600,
              color: i < atual ? 'var(--primary)' : i === atual ? '#000' : 'var(--text-color)',
              opacity: i > atual ? 0.6 : 1,
            }}
          >
            <span>{i < atual ? '✓' : i === atual ? '●' : '○'}</span>
            <span>{p.texto}</span>
          </div>
        ))}
      </div>
      {/* `.fdc-barra-listrada` ja existia no styles.css (o HUD do Monitor usa) — ela traz
          o gradiente e a animacao das listras. Nao criar outra. */}
      <div style={{ flex: 1, height: 12, borderRadius: 7, background: 'var(--page-bg)', overflow: 'hidden' }}>
        <div className="fdc-barra-listrada" style={{ width: largura, height: '100%', borderRadius: 7, transition: 'width .4s ease' }} />
      </div>
      <span style={{ flex: '0 0 auto', fontSize: 11, color: 'var(--text-color)', opacity: 0.8 }}>Nao feche esta tela</span>
    </div>
  );
}
